import React, { createContext, useEffect, useState } from "react";
import styled from "styled-components";
import LeaveCalendar from "./LeaveCalendar";
import { Radio } from "antd";
import EngagementCalendar from "./EngagementCalendar";
import EventCalendar from "./EventCalendar";
import MyPlannerCalendar from "./MyPlannerCalendar";
import { useSelector } from "react-redux";
import {
  getClientid,
  getMenuList,
  getRoleList,
  getUserInfo,
  getUserList,
  getUserRoleId,
} from "redux/selectors";
import { useFetch } from "hooks/useFetch";
import queryKeys from "common/queryKey";
import {
  addMonthinDate,
  getFirstDate,
  getKenyanDateTime,
  getLastDate,
  momentDate,
} from "utils/Date";
import LeaveWeekCalendar from "pages/WeekCalendarView/LeaveWeekCalendar";

const MY_PLANNER = "myplanner";
const LEAVE = "leave";
const ENGAGEMENT = "engagement";
const EVENT = "event";


export const CalendarContext = createContext();

const Calendar = () => {
  const [calendarType, setCalendarType] = useState(MY_PLANNER);
  const [tabId, setTabId] = useState("Mine");
  // Shared Week/Month view, kept in sync across calendar types so switching
  // between My Planner / Event / Leave / Matter preserves the chosen view.
  const [calendarView, setCalendarView] = useState("timeGridWeek");
  const [miniCalendarCurrentDate, setMiniCalendarCurrentDate] = useState(
    getKenyanDateTime()
  );

  const menuList = useSelector(getMenuList);
  const userRoleId = useSelector(getUserRoleId);
  const userList = useSelector(getUserList);
  const userInfo = useSelector(getUserInfo);
  const clientId = useSelector(getClientid);
  const roleList = useSelector(getRoleList);
  const [refetchEvents, setRefetchEvents] = useState(() => () => { });

  let Menulist = menuList[userRoleId] || Object.values(menuList)[0] || menuList;

  Menulist = Array.isArray(Menulist) ? Menulist : [];

  const leaveTabs = Menulist?.filter(
    (item) =>
      item.key === "mycalendar" ||
      item.key === "teamcalendar" ||
      item.key === "allcalendar"
  );

  const engagementTabs = Menulist?.filter(
    (item) =>
      item.key === "myengagementcalendar" ||
      item.key === "teamengagementcalendar" ||
      item.key === "allengagementcalendar"
  );

  const eventTabs = Menulist?.filter(
    (item) => item.key === "myeventcalendar"
  );

  const leaveAccess = leaveTabs?.some((item) => item.permission.view);

  const engagementAccess = engagementTabs?.some((item) => item.permission.view);

  const eventAllPermission = eventTabs?.find((item) => item.subtitle === "All")?.permission;
  const eventMinePermission = eventTabs?.find((item) => item.subtitle === "Mine")?.permission;

  useEffect(() => {
    setCalendarType(MY_PLANNER);
  }, [leaveAccess, engagementAccess]);

  // Event calendar only has "Mine" / "All" tabs — Leave/Matter also have
  // "Team". If the user was on "Team" and switches the top-level radio
  // to Event, fall back to "Mine" instead of silently behaving like "All".
  useEffect(() => {
    if (calendarType === EVENT && tabId !== "Mine" && tabId !== "All") {
      setTabId("Mine");
    }
  }, [calendarType]);

  const currentActiveAccess = leaveAccess ? (
    <div className="fw-bold">LEAVE</div>
  ) : (
    <div className="fw-bold">ENGAGEMENT</div>
  );

  const previousMonth = () =>
    setMiniCalendarCurrentDate(
      momentDate(addMonthinDate(miniCalendarCurrentDate, "-1"))
    );

  const nextMonth = () =>
    setMiniCalendarCurrentDate(
      momentDate(addMonthinDate(miniCalendarCurrentDate, "1"))
    );



  const currentUser = userList?.find(
    (item) => item?.userid === userInfo.userid
  );

  const { key, url } = queryKeys.getHolidayList(clientId);

  const { data: holidayDataRes, refetch } = useFetch(
    key,
    url,
    {
      enabled: !!clientId,
    },
    {
      start_date: getFirstDate(miniCalendarCurrentDate),
      end_date: getLastDate(miniCalendarCurrentDate),
    }
  );

  useEffect(() => {
    refetch();
  }, [miniCalendarCurrentDate])

  const baselocationid = currentUser?.baselocationid;

  const holidayList = holidayDataRes?.response?.filter((item) =>
    item?.location_id?.includes(baselocationid)
  );

  const filterrole = roleList?.find((item) => item?.roleId === userRoleId);
  const filterlocationid = filterrole?.locationId;
  const allHolidayList = holidayDataRes?.response?.filter((item) =>
    item?.location_id?.some((id) => filterlocationid?.includes(id))
  );

  const radioCalendarSelect = (
    <Radio.Group
      size="middle"
      buttonStyle="solid"
      value={calendarType}
      onChange={(e) => setCalendarType(e.target.value)}
    >
      <Radio.Button value={MY_PLANNER}>My Planner</Radio.Button>
      <Radio.Button value={EVENT}>Event</Radio.Button>
      <Radio.Button value={LEAVE}>Leave</Radio.Button>
      <Radio.Button value={ENGAGEMENT}>Matter</Radio.Button>
    </Radio.Group>
  );

  return (
    <CalendarContext.Provider
      value={{
        tabBarExtraContent: {
          right: radioCalendarSelect,
        },
        miniCalendarCurrentDate: miniCalendarCurrentDate,
        holidayList: holidayList,
        allHolidayList: allHolidayList,
        previousMonth: previousMonth,
        nextMonth: nextMonth,
        refetchEvents,        // ← add
        setRefetchEvents,
        eventAllPermission,
        eventMinePermission,
        calendarView,
        setCalendarView,
      }}
    >
      <StyledCalendar>
        {calendarType === MY_PLANNER && (
          <MyPlannerCalendar />
        )}
        {calendarType === LEAVE && (
          <LeaveCalendar tabId={tabId} setTabId={setTabId} />
        )}
        {calendarType === ENGAGEMENT && (
          <EngagementCalendar tabId={tabId} setTabId={setTabId} />
        )}
        {calendarType === EVENT && (
          <EventCalendar tabId={tabId} setTabId={setTabId} calendarType={calendarType} />
        )}
      </StyledCalendar>
    </CalendarContext.Provider>
  );
};

export default Calendar;
export const StyledCalendar = styled.div`
  /* ── Keep the Mine/All tab row the same height as My Planner's header
        so the date-nav + grid start at the same Y on every tab ── */
  .ant-tabs-nav {
    min-height: 50px;
    margin: 0 0 16px 0 !important;
  }
  .ant-tabs-nav::before {
    display: none;
  }

  /* ── Major tab group (Frame 2147229022) ── */
  .ant-radio-group {
    display: inline-flex;
    align-items: center;
    height: 34px;
    border: 1px solid #dceded;
    border-radius: 20px;
    overflow: hidden;
    background: #ffffff;
  }

  .ant-radio-button-wrapper {
    height: 34px;
    line-height: 34px;
    padding: 0 18px;
    border: none;
    border-right: 1px solid #dceded;
    background: #ffffff;
    box-shadow: 0px 4px 58px rgba(45, 83, 218, 0.05);
    font-family: "DM Sans", sans-serif;
    font-weight: 400;
    font-size: 16px;
    color: #1f243b;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 0.15s, color 0.15s;
  }

  /* remove Ant's built-in 1px separator pseudo-element */
  .ant-radio-button-wrapper::before {
    display: none !important;
  }

  .ant-radio-button-wrapper:first-child {
    border-radius: 24px 0 0 24px;
  }

  .ant-radio-button-wrapper:last-child {
    border-right: none;
    border-radius: 0 24px 24px 0;
  }

  .ant-radio-button-wrapper:hover {
    color: #00685f;
  }

  /* ── Active tab (Frame 86) ── */
  .ant-radio-button-wrapper-checked:not(.ant-radio-button-wrapper-disabled),
  .ant-radio-button-wrapper-checked:not(
      .ant-radio-button-wrapper-disabled
    ):hover {
    background: #deefee !important;
    border-color: #deefee !important;
    color: #00685f !important;
    font-weight: 600 !important;
    box-shadow: 0px 4px 58px rgba(45, 83, 218, 0.05) !important;
  }

  .ant-radio-button-wrapper-checked:not(
      .ant-radio-button-wrapper-disabled
    )::before {
    display: none !important;
  }
`;